"use client"

import type React from "react"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { addProductReview } from "@lib/data/reviews"
import { Star } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"

export default function ProductReviewForm({ productId }: { productId: any }) {
    const { t } = useTranslation()
    const [rating, setRating] = useState<number>(0)
    const [hoveredRating, setHoveredRating] = useState<number>(0)
    const [comment, setComment] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)

        try {
            const response = await addProductReview(productId, comment, rating)

            if (response?.success) {
                toast.success(t("product.reviewSubmittedSuccess"))
                setRating(0)
                setComment("")
            } else {
                const errorMessage = response?.error || t("product.reviewError")
                toast.error(errorMessage)
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t("product.reviewErrorGeneric")
            toast.error(errorMessage)
            console.error("Review submission error:", error)
        } finally {
            setIsSubmitting(false)
        }
    }


    return (
        <div className="w-full flex flex-col gap-4 items-start">
            <form onSubmit={handleSubmit} className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="space-y-2">
                    <div className="space-y-2">
                        <Label htmlFor="rating" className="text-sm font-medium text-slate-700">{t("product.rating")}</Label>
                        <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    className="focus:outline-none focus-visible:ring focus-visible:ring-ring focus-visible:ring-offset-2 p-1"
                                    onClick={() => setRating(star)}
                                    onMouseEnter={() => setHoveredRating(star)}
                                    onMouseLeave={() => setHoveredRating(0)}
                                    aria-label={t("product.rateStars", { count: star })}
                                >
                                    <Star
                                        size={18}
                                        className={cn(
                                            "transition-all",
                                            (hoveredRating ? hoveredRating >= star : rating >= star)
                                                ? "fill-[#ffc107] text-[#ffc107]"
                                                : "fill-muted text-muted-foreground",
                                        )}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="review" className="text-sm font-medium text-slate-700">{t("product.yourReview")}</Label>
                        <Textarea
                            id="review"
                            placeholder={t("product.reviewPlaceholder")}
                            rows={1}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            required
                            className="border-slate-200 focus-visible:ring-orange-200"
                        />
                    </div>
                    <div className="flex justify-between mt-2">
                        <Button type="submit" disabled={isSubmitting || rating === 0 || !comment} className="rounded-xl border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100">
                            {isSubmitting ? t("product.submitting") : t("product.submitReview")}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    )
}
